import { z } from 'zod';
import { roleSchema } from '../context.js';
export const signupRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(12, 'Password must be at least 12 characters'),
    householdName: z.string().min(1).max(120),
    displayName: z.string().min(1).max(120),
});
export const loginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
export const tokenPairSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number().int(),
    tokenType: z.literal('Bearer'),
});
export const refreshRequestSchema = z.object({ refreshToken: z.string().min(1) });
export const apiKeyExchangeSchema = z.object({
    key: z.string().min(1),
    secret: z.string().min(1),
});
export const createApiKeyRequestSchema = z.object({ label: z.string().min(1).max(120) });
export const apiKeyCreatedSchema = z.object({
    id: z.string().uuid(),
    key: z.string(),
    secret: z.string(),
    label: z.string(),
});
export const inviteRequestSchema = z
    .object({
    email: z.string().email(),
    role: roleSchema,
    /** Needed only when creating a new person; the existing one has a name. */
    displayName: z.string().min(1).max(120).optional(),
    /**
     * An existing person for this login to claim, rather than a second record
     * for the same human (ADR-015). Must be somebody nobody has claimed yet.
     */
    memberId: z.string().uuid().nullish(),
})
    .refine((v) => Boolean(v.memberId) || Boolean(v.displayName), {
    message: 'Say who they are, or which person they already are',
    path: ['displayName'],
});
export const acceptInviteRequestSchema = z.object({
    token: z.string().min(1),
    password: z.string().min(12),
});
export const forgotPasswordRequestSchema = z.object({ email: z.string().email() });
export const resetPasswordRequestSchema = z.object({
    token: z.string().min(1),
    password: z.string().min(12, 'Password must be at least 12 characters'),
});
/** Change your own password, knowing the current one (M-02). */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(12, 'Password must be at least 12 characters'),
});
/** Which ways in this server offers besides email and password. */
export const authProvidersSchema = z.object({
    google: z.boolean(),
});
/** Household-wide settings an admin may change (M-01). */
export const tenantSettingsSchema = z.object({
    /** IANA zone name; every calendar date in the household is reckoned in it. */
    timezone: z.string().min(1).max(64),
});
export const userSummarySchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: roleSchema,
    status: z.enum(['pending', 'active', 'disabled']),
    displayName: z.string().nullable(),
});
// ---------------------------------------------------------- passkeys --
// S-01, ADR-038. The WebAuthn JSON itself is checked by the server's
// WebAuthn library, not here: these schemas carry it through untouched.
const webauthnJson = z.object({}).passthrough();
/** Options for one ceremony, and the challenge they were made with. */
export const passkeyOptionsSchema = z.object({
    challengeId: z.string().uuid(),
    options: webauthnJson,
});
export const registerPasskeySchema = z.object({
    challengeId: z.string().uuid(),
    /** What to call it, so it can be told from the others later. */
    name: z.string().trim().min(1).max(60),
    response: webauthnJson,
});
export const passkeyLoginSchema = z.object({
    challengeId: z.string().uuid(),
    response: webauthnJson,
});
export const passkeySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Synced to the person's password manager or account, rather than tied to one device. */
    backedUp: z.boolean(),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
});
//# sourceMappingURL=auth.js.map